/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { zoomTime } from '../utils/timeZoomUtils';
import { KbZoomEvent } from '../utils/keyboardUtils';
import { OnTimeComponentViewportChange, TimeComponentZoomParameters } from '../TimeComponent.types';

type UseKbZoomOptions = {
  startTime: number;
  endTime: number;
  viewportStartTime: number;
  viewportEndTime: number;
  majorAxisZoomOrder?: TimeComponentZoomParameters['majorAxisZoomOrder'];
  majorAxisZoomOrderIndex?: TimeComponentZoomParameters['majorAxisZoomOrderIndex'];
  minorAxisZoomOrder: TimeComponentZoomParameters['minorAxisZoomOrder'];
  minorAxisZoomOrderIndex: TimeComponentZoomParameters['minorAxisZoomOrderIndex'];
  zoomOrderViewDurationThresholds: ReturnType<
    TimeComponentZoomParameters['getZoomOrderViewDurationThresholds']
  >;
  minZoomViewportDuration: TimeComponentZoomParameters['minZoomViewportDuration'];
  maxZoomViewportDuration: TimeComponentZoomParameters['maxZoomViewportDuration'];
  onViewportChange?: OnTimeComponentViewportChange;
  isDisabled?: boolean;
};

/**
 * Hook that enables and handles keyboard zooming gestures.
 */
const useKbZoom = ({
  startTime,
  endTime,
  viewportStartTime,
  viewportEndTime,
  majorAxisZoomOrder,
  majorAxisZoomOrderIndex,
  minorAxisZoomOrder,
  minorAxisZoomOrderIndex,
  zoomOrderViewDurationThresholds,
  minZoomViewportDuration,
  maxZoomViewportDuration,
  onViewportChange,
  isDisabled = false
}: UseKbZoomOptions) => {
  const onKbZoomIn = (e: KbZoomEvent) => {
    const viewportCenter = viewportStartTime + (viewportEndTime - viewportStartTime) / 2;
    zoomTime(
      viewportCenter,
      viewportStartTime,
      viewportEndTime,
      startTime,
      endTime,
      e.detail.zoomFactor,
      minZoomViewportDuration,
      maxZoomViewportDuration,
      zoomOrderViewDurationThresholds,
      minorAxisZoomOrder,
      minorAxisZoomOrderIndex,
      majorAxisZoomOrder,
      majorAxisZoomOrderIndex,
      onViewportChange
    );
  };

  const onKbZoomOut = (e: KbZoomEvent) => {
    const viewportCenter = viewportStartTime + (viewportEndTime - viewportStartTime) / 2;
    zoomTime(
      viewportCenter,
      viewportStartTime,
      viewportEndTime,
      startTime,
      endTime,
      e.detail.zoomFactor,
      minZoomViewportDuration,
      maxZoomViewportDuration,
      zoomOrderViewDurationThresholds,
      minorAxisZoomOrder,
      minorAxisZoomOrderIndex,
      majorAxisZoomOrder,
      majorAxisZoomOrderIndex,
      onViewportChange
    );
  };

  return isDisabled
    ? {}
    : {
        onKbZoomIn,
        onKbZoomOut
      };
};

export { useKbZoom };
